import type { ChangesModel } from '@/entities/optimization'
import type { HTTPValidationError, OptimizationModel } from '@/shared/generated'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useOptimizationQuery } from '@/entities/optimization'

import { client } from '@/shared/api/client'
import { clarify } from '@/shared/components/ui/clarify'
import { confirm } from '@/shared/components/ui/confirmer'
import { OptimizationAction, OptimizationStatus } from '@/shared/generated'

import {
	getOnlyFinalFields,
	isEditedFinalFields,
	isEditedModelFields,
} from './helpers'

type UpdateProductsFieldsPayload = {
	changes: ChangesModel[]
	ignore_empties: boolean
	file_header_mapping?: Record<string, string>
}
export function useUpdateProductsFieldsMutation(
	optimizationGroupId: string,
	options?: UseMutationOptions<
		OptimizationModel | null | undefined,
		AxiosError<HTTPValidationError>,
		UpdateProductsFieldsPayload
	>
) {
	const { t } = useTranslation()

	const { data: optimization } = useOptimizationQuery(optimizationGroupId, {
		enabled: !!optimizationGroupId,
	})

	return useMutation<
		OptimizationModel | null | undefined,
		AxiosError<HTTPValidationError>,
		UpdateProductsFieldsPayload
	>({
		mutationFn: async ({ changes, ignore_empties }) => {
			const isFinalFieldsChanged = isEditedFinalFields(changes)
			const isModelFieldsChanged = isEditedModelFields(changes)
			if (
				[OptimizationStatus.Finished, OptimizationStatus.Failed].includes(
					optimization!.status
				)
			) {
				if (isModelFieldsChanged && isFinalFieldsChanged) {
					const saveStartegy = await clarify({
						title: t('products_saving_or_final_title'),
						description: t('products_saving_model_or_final'),
						actions: [
							{ actionId: 'model', actionText: t('general_apply') },
							{
								actionId: 'final',
								actionText: t('apply_only_final_price_changes'),
							},
						],
					})
					if (saveStartegy === 'model') {
						await client.optimizations.optimizationAction({
							action: OptimizationAction.Reset,
							optimizationGroupId,
						})

						return client.optimizations.optimizationProductDiffsSku(
							{ optimizationGroupId },
							changes
						)
						/* return client.products.updateProductDiffs(
							{ optimizationGroupId, optimizationId: 'last' },
							{
								ignore_empties,
								product_diffs: changes,
							}
						) */
					} else if (saveStartegy === 'final') {
						return client.optimizations.optimizationFinalFieldsSku(
							{ optimizationGroupId },
							getOnlyFinalFields(changes) as ChangesModel[]
						)
						/* return client.products.updateFinalFields(
							{ optimizationGroupId, optimizationId: 'last' },
							{
								final_fields: getOnlyFinalFields(changes) as ChangesModel[],
							}
						) */
					}
				} else if (isModelFieldsChanged) {
					const confirmation = await confirm({
						title: t('products_saving_title'),
						description: t('products_saving_model'),
						actionText: t('general_save_changes'),
						cancelText: t('general_cancel'),
					})
					if (confirmation) {
						await client.optimizations.optimizationAction({
							action: OptimizationAction.Reset,
							optimizationGroupId,
						})

						return client.optimizations.optimizationProductDiffsSku(
							{ optimizationGroupId },
							changes
						)
						/* return client.products.updateProductDiffs(
							{ optimizationGroupId, optimizationId: 'last' },
							{
								ignore_empties,
								product_diffs: changes,
							}
						)*/
					}
				} else {
					return client.optimizations.optimizationFinalFieldsSku(
						{ optimizationGroupId },
						getOnlyFinalFields(changes) as ChangesModel[]
					)
					/* return client.products.updateFinalFields(
						{ optimizationGroupId, optimizationId: 'last' },
						{
							final_fields: getOnlyFinalFields(changes) as ChangesModel[],
						}
					)*/
				}
			} else {
				return client.optimizations.optimizationProductDiffsSku(
					{ optimizationGroupId },
					changes
				)
				/* return client.products.updateProductDiffs(
					{ optimizationGroupId, optimizationId: 'last' },
					{
						ignore_empties,
						product_diffs: changes,
					}
				) */
			}
		},
		...(options || {}),
	})
}
